import React, { useState, useEffect } from 'react';
import { mintNumberToName } from 'utils/token';
import { Link } from 'react-router-dom'
import { getRarity } from 'utils/metadata';
import { usePlayerContext } from '../Player/PlayerContext';
import { Box, ClickableFlex, EthSign, Flex } from '../ui';
import BurnSign from '../ui/BurnSign';
import PrintsSupply from './PrintsSupply';
import { ReactComponent as Record } from 'img/record.svg';
import get from 'lodash/get';
import { useTheme } from 'styled-components';
import { Original } from '../common';
import { getDropFromContractAddress } from 'utils/dropsHelpers';
import { useMemo } from 'react';
import usePrintsData from 'hooks/usePrintsData';
import EulerBeatsWrapper from 'utils/EulerBeatsWrapper';
import { futuraContractAddress } from 'utils/config';

interface AlbumArtProps {
  imgUrl: string;
  ml?: number | string;
}
interface BeatRowProps {
  original: Original;
}

export const AlbumArt = React.memo((props: AlbumArtProps) => {
  const { imgUrl, ml } = props;
  const [showDefaultImage, setShowDefaultImage] = useState(false);
  const { colors } = useTheme();

  useEffect(() => {
    if (!imgUrl) {
      setShowDefaultImage(true);
    } else {
      setShowDefaultImage(false);
    }
  }, [imgUrl]);

  const handleBrokenImage = (event: any) => {
    console.log('broken image');
    setShowDefaultImage(true);
  };

  return (
    <Box ml={ml}>
      {!showDefaultImage && (
        <img src={imgUrl} width="60px" alt="Original NFT" onError={handleBrokenImage} />
      )}
      {showDefaultImage && <Record width="60px" height="60px" fill={colors.primary} />}
    </Box>
  );
});

const BeatRow: React.FC<BeatRowProps> = ({ original }) => {
  const { dispatch, activeBeat } = usePlayerContext();
  const { printsData } = usePrintsData(original.id);
  const { colors } = useTheme();
  const isFutura = activeBeat?.contractAddress === futuraContractAddress;
 
  const isActiveBeat = useMemo(() => {
    return (
      activeBeat &&
      activeBeat.id === original.id &&
      activeBeat.contractAddress === original.contractAddress
    );
  }, [activeBeat, original.id, original.contractAddress]);

  if (!activeBeat) {
    return null;
  }

  return (
    <ClickableFlex
      color="copy"
      onClick={() =>
        dispatch({
          type: 'select',
          beat: original,
          autoPlayNext: EulerBeatsWrapper.visual.isPlaying(),
        })
      }
      alignCenter
      py="2"
      pl={isActiveBeat ? '17px' : '24px'}
      backgroundColor={isActiveBeat ? 'rgba(255, 255, 255, 0.1)' : 'transparent'}
      pr="24px"
      borderLeft={isActiveBeat ? '7px solid' : ''}
      borderColor={isActiveBeat ? 'highlight' : ''}
    >
      <AlbumArt imgUrl={get(original, 'media.image', '')} />

      <Flex column ml="3" width="100%">
        <Flex width="100%" justifyBetween alignCenter>
          <Box color="highlight" fontWeight="700">
            {mintNumberToName(
              original.mintNumber,
              getDropFromContractAddress(original.contractAddress)
            )}
          </Box>
          {
            isFutura && (
              <Link to={`/futura/details/${activeBeat.id}`}>
                <Box borderRadius="8px" color="white" py={2} px={3} backgroundColor="#222">
                  Detailed view
                </Box>
              </Link>
            )
          }
          {!isFutura && (
            <Box color="highlight" fontSize="13px">
              {getRarity(original)}
            </Box>
          )}
        </Flex>
        { !isFutura && (
          <Flex color="copy" alignCenter justifyBetween width="100%" py={['2', '2', 0]}>
            
            <PrintsSupply showLabel supply={printsData ? printsData.supply : original.print?.supply} />
          
            <Flex column>
              <Box fontSize="12px" display={['none', 'none', 'block']} mr="2">
                Print Price
              </Box>
              <Flex alignCenter>
                <EthSign
                  color="copy"
                  value={printsData ? printsData.nextPrintPrice : original!.print!.nextPrintPrice}
                />
                <Box
                  background={colors.highlight}
                  color="white"
                  width="16px"
                  height="16px"
                  fontSize="11px"
                  padding="0 5px"
                  margin="0 0 0 4px"
                  borderRadius="50%"
                >
                  P
                </Box>
              </Flex>
            </Flex>

            <Flex column>
              <Box fontSize="12px" display={['none', 'none', 'block']} mr="2">
                Burn Price
              </Box>
              <BurnSign burnPrice={printsData ? printsData.burnPrice : original!.print!.burnPrice} />
            </Flex>
          </Flex>
        )}
      </Flex>
    </ClickableFlex>
  );
};

export default BeatRow;
